import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import useGetSRTProfileRegions from './queries/useGetSRTProfileRegions';
import useGetSRTProfileTrunkTypes from './queries/useGetSRTProfileTrunkTypes';
import useGetSRTServiceProfile from './queries/useGetSRTServiceProfile';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import { multiSelectDataMapper, toSentenceCase } from 'utils/utils';
import config from '@/config.json';
import FormBase from '@/components/forms/new/FormBase';
import { Field } from 'formik';
import { validationSchemaSRT } from './schema';
import SBCaaSVisibleScopeField from './formFields/SBCaaSVisibleScopeField';
import SBCaaSRegionsField from './formFields/SBCaaSRegionsField';
import SRTTrunkTypesField from './formFields/SRTTrunkTypesField';
import SRTMaxChannelField from './formFields/SRTMaxChannelField';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import {
    SRT_TRUNK_TYPE_ID,
    SRT_PROFILE_MIN_CHANNELS,
    SRT_PROFILE_MAX_CHANNELS,
    srtTrunkTypeName,
} from '@/constants/srt';
import { SBCAAS_MODERN_LABEL } from '@/constants/sbcaas';
import { resolvePartnerName } from './ownerName';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';
const SRTServiceProfile = ({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { data: currentCompanyData } = useGetCurrentCompany();

    const partnerName = resolvePartnerName({
        companiesAndPartners,
        currentCompanyData,
        currentPartner: partnerID,
    });
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);

    // Resolve the owning wholesaler/partner name up front, falling back to the
    // bare profile name so the header never renders a placeholder while the
    // navigation lists are still loading
    const getInitialName = () => {
        if (data?.name) return data.name;
        if (level === 'partner') {
            return partnerName
                ? `${partnerName} - ${SBCAAS_MODERN_LABEL} Partner Profile`
                : `${SBCAAS_MODERN_LABEL} Partner Profile`;
        }
        return resolveWholesalerProfileName(
            currentWholesalerData?.name,
            SBCAAS_MODERN_LABEL,
        );
    };

    const DEFAULT_SRT_PAYLOAD = {
        name: getInitialName(),
        maxChannel: 10,
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        serviceType: 'SRT',
        visibleScope: level === 'partner' ? 20 : 60,
        regions: [],
        trunkTypes: [],
    };

    useEffect(() => {
        if (mode === 'EDIT') {
            return;
        }
        if (level === 'partner') {
            if (partnerName) {
                handleChangeState({
                    name: `${partnerName} - ${SBCAAS_MODERN_LABEL} Partner Profile`,
                });
            }
        } else if (currentWholesalerData?.name) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData.name,
                    SBCAAS_MODERN_LABEL,
                ),
            });
        }
    }, [
        mode,
        partnerName,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
    ]);

    const [modalState, setModalState] = useState(data || DEFAULT_SRT_PAYLOAD);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // The API route segment is PascalCase (`/ServiceProfile/SRT/Wholesaler`);
    // `level` stays lowercase because the rest of the page keys off it.
    const scopeLevel = level === 'partner' ? 'Partner' : 'Wholesaler';

    const scope =
        level === 'partner'
            ? `Partner/${data?.partnerID || partnerID}`
            : `Wholesaler/${data?.wholesalerID || wholesalerID}`;

    const { data: availableRegions = [], isLoading: regionsLoading } =
        useGetSRTProfileRegions({ scope });

    const { data: availableTrunkTypes = [], isLoading: trunkTypesLoading } =
        useGetSRTProfileTrunkTypes({ level, partnerID });

    useGetSRTServiceProfile({
        scope,
        mode,
        onSuccess: (spResult) => {
            setModalState({
                ...spResult.data,
                regions: (spResult.data.regions || []).map(
                    multiSelectDataMapper,
                ),
            });
        },
    });

    const handleChangeState = (payload) => {
        setModalState((prev) => ({ ...prev, ...payload }));
    };

    if (regionsLoading || trunkTypesLoading) {
        return <LoadingIndicator />;
    }

    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };

    // The wholesaler-level list is platform-agnostic, so narrow it to the SRT
    // trunk type. At partner level the endpoint already scopes to SRT.
    const srtTrunkTypes = availableTrunkTypes.filter(
        ({ id }) => id === SRT_TRUNK_TYPE_ID,
    );

    // The trunk type is named "SRT" by the backend. The saved payload keeps
    // that name; only what the user reads is relabelled.
    const srtTrunkTypeOptions = srtTrunkTypes.map((type) => ({
        ...type,
        name: srtTrunkTypeName(type),
    }));

    const initialValues = {
        name: modalState?.name || '',
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
        regions: mapToOptions(modalState?.regions, availableRegions),
        // Set for the user — the field renders read-only, so this is the only
        // thing that ever populates it
        trunkTypes: srtTrunkTypes,
        maxChannel: modalState?.maxChannel ?? 10,
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        serviceType: 'SRT',
    };

    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);

        const finalSubmitValues = { ...modalState, ...values };
        try {
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'SRT',
                    payload: finalSubmitValues,
                    level: scopeLevel,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'SRT',
                    payload: values,
                    level: scopeLevel,
                    scope: requiredScope,
                });
                handleBackButton();
            }
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}
                validationSchema={validationSchemaSRT}>
                {() => [
                    <Field key="visibleScope" name="visibleScope">
                        {(fieldProps) => (
                            <SBCaaSVisibleScopeField
                                {...fieldProps}
                                options={visibleScopeOptions}
                                requiredScope={requiredScope}
                                config={config}
                                darkMode={darkMode}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="trunkTypes" name="trunkTypes">
                        {(fieldProps) => (
                            <SRTTrunkTypesField
                                {...fieldProps}
                                options={srtTrunkTypeOptions}
                                darkMode={darkMode}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="regions" name="regions">
                        {(fieldProps) => (
                            <SBCaaSRegionsField
                                {...fieldProps}
                                options={availableRegions}
                                darkMode={darkMode}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    <Field key="maxChannel" name="maxChannel">
                        {(fieldProps) => (
                            <SRTMaxChannelField
                                {...fieldProps}
                                darkMode={darkMode}
                                minAllowed={SRT_PROFILE_MIN_CHANNELS}
                                maxAllowed={SRT_PROFILE_MAX_CHANNELS}
                                minWidth="420px"
                                maxWidth="520px"
                                width="100%"
                            />
                        )}
                    </Field>,
                    error && (
                        <Box paddingBottom="0.5rem" key="error-box-srt">
                            <Alert
                                key="error"
                                status="error"
                                mt={4}
                                marginBottom="1rem">
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        </Box>
                    ),
                ]}
            </FormBase>
        </>
    );
};

export default SRTServiceProfile;
